/**
 * Utility functions for handling property type/subtype splitting and cascading dropdowns
 */

/**
 * Splits property type and subtype from a combined value
 * Example: 'Housing - Apartment/Multi-Family Housing' => { type: 'Housing', subtype: 'Apartment/Multi-Family Housing' }
 * @param {string} fullValue - The combined value
 * @returns {object} Object with 'type' and 'subtype' properties
 */
export const splitPropertyTypeAndSubtype = (fullValue) => {
  if (!fullValue) return { type: '', subtype: '' };

  const dashIndex = fullValue.indexOf(' - ');
  if (dashIndex !== -1) {
    return {
      type: fullValue.substring(0, dashIndex).trim(),
      subtype: fullValue.substring(dashIndex + 3).trim(),
    };
  }

  return { type: fullValue, subtype: '' };
};

/**
 * Groups property options by type/category
 * Example: Input: [{value: 'Housing - Apartment', ...}, {value: 'Housing - Condo', ...}]
 * Output: { 'Housing': [{value: 'Housing - Apartment', ...}, {value: 'Housing - Condo', ...}] }
 * @param {array} propertyOptions - Array of property option objects
 * @returns {object} Grouped options by property type
 */
export const groupPropertyTypesByCategory = (propertyOptions = []) => {
  const grouped = {};

  propertyOptions.forEach((option) => {
    const { type } = splitPropertyTypeAndSubtype(option.value);

    if (type && option.value !== '') {
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(option);
    }
  });

  return grouped;
};

/**
 * Extracts unique property types from grouped data
 * @param {object} groupedData - Grouped property data
 * @returns {array} Array of property type options for dropdown
 */
export const getPropertyTypes = (groupedData = {}) => {
  return Object.keys(groupedData).map((type) => ({
    value: type,
    display_name: type,
  }));
};

/**
 * Gets subtype options for a selected property type
 * @param {object} groupedData - Grouped property data
 * @param {string} propertyType - Selected property type
 * @returns {array} Array of subtype options for dropdown
 */
export const getSubtypesByType = (groupedData = {}, propertyType = '') => {
  if (!propertyType || !groupedData[propertyType]) {
    return [];
  }

  return groupedData[propertyType].map((option) => {
    const { subtype } = splitPropertyTypeAndSubtype(option.value);
    
    // Add '(Retired)' suffix if subtype contains 'Data Center' (case-insensitive)
    const displayName = subtype.toLowerCase().includes('data center') 
      ? `${subtype} (Retired)` 
      : subtype;
    
    return {
      value: subtype,
      display_name: displayName,
      fullValue: option.value, // Keep full value for reference
    };
  });
};
